import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  ClipboardList,
  HelpCircle,
  KeyRound,
  Share2,
  Wallet,
  Warehouse,
} from 'lucide-react';
import {
  carregarEstadoBarbearia,
  persistirEstadoBarbearia,
} from '../../utils/barbeariaPersistencia';
import { estadoInicialBarbearia, isModoClientePublico, aplicarCatalogoOficial } from '../../utils/barbeariaLogica';
import {
  mesclarEstadosBarbearia,
  publicarEstadoNuvem,
  puxarEstadoNuvem,
} from '../../utils/barbeariaSyncNuvem';
import { tourJaConcluido } from '../../utils/barbeariaTour';
import type { BarbeariaEstado, VistaBarbearia } from '../../utils/barbeariaTipos';
import BarbeariaAgendamento from './BarbeariaAgendamento';
import BarbeariaAdmin from './BarbeariaAdmin';
import BarbeariaFinanceiro from './BarbeariaFinanceiro';
import BarbeariaGastos from './BarbeariaGastos';
import BarbeariaTour from './BarbeariaTour';
import BarbeariaCabecalhoMarca from './BarbeariaCabecalhoMarca';
import BarbeariaLembretesWhatsApp from './BarbeariaLembretesWhatsApp';
import BarbeariaLicencaAtivacao from './BarbeariaLicencaAtivacao';
import BarbeariaPainelLicencas from './BarbeariaPainelLicencas';
import {
  isNwDesktopVendedor,
  licencaLocalValida,
  precisaLicencaLoja,
} from '../../utils/barbeariaLicenca';

const VISTAS_BASE: Array<{ id: VistaBarbearia; label: string; icon: typeof CalendarDays }> = [
  { id: 'agendamento', label: 'Agendar', icon: CalendarDays },
  { id: 'admin', label: 'Agenda', icon: ClipboardList },
  { id: 'financeiro', label: 'Financeiro', icon: Wallet },
  { id: 'gastos', label: 'Gastos', icon: Warehouse },
];

function montarLinkCliente(estado: BarbeariaEstado): string {
  const salvo = (estado.config.linkPublicoAgendamento || '').trim();
  if (salvo) return salvo.replace(/\?agendar=1&?/g, '').replace(/[?&]$/, '') || salvo;
  return 'https://carioca-barbershop.pages.dev/';
}

export default function BarbeariaApp() {
  const modoCliente = useMemo(() => isModoClientePublico(), []);
  /** Só o atalho local (nw-app com vendedor.local.json) */
  const modoVendedor = useMemo(() => isNwDesktopVendedor(), []);
  const vistas = useMemo(() => {
    if (!modoVendedor) return VISTAS_BASE;
    return [
      ...VISTAS_BASE,
      { id: 'licencas' as const, label: 'Licenças', icon: KeyRound },
    ];
  }, [modoVendedor]);
  const exigeLicenca = useMemo(() => precisaLicencaLoja(), []);
  const [licencaOk, setLicencaOk] = useState(() => !precisaLicencaLoja() || licencaLocalValida());
  const [estado, setEstado] = useState<BarbeariaEstado>(() => estadoInicialBarbearia());
  const [vista, setVista] = useState<VistaBarbearia>('agendamento');
  const [prontoParaSalvar, setProntoParaSalvar] = useState(false);
  const [avisoLink, setAvisoLink] = useState('');
  const [tourAtivo, setTourAtivo] = useState(false);
  const carregouRef = useRef(false);
  const tourAutoRef = useRef(false);
  /** Link cliente: ativo = sync; confirmado = parou após pedido ok; expirado = inativo. */
  const [fluxoCliente, setFluxoCliente] = useState<'ativo' | 'confirmado' | 'expirado'>('ativo');
  const ultimaAtividadeRef = useRef(Date.now());
  const appLiberado = !exigeLicenca || licencaOk;

  useEffect(() => {
    if (!appLiberado) return;
    let cancelado = false;
    (async () => {
      const local = await carregarEstadoBarbearia();
      const remoto = await puxarEstadoNuvem();
      const carregado = aplicarCatalogoOficial(mesclarEstadosBarbearia(local, remoto));
      if (cancelado) return;
      setEstado(carregado);
      carregouRef.current = true;
      setProntoParaSalvar(true);
    })();
    return () => {
      cancelado = true;
    };
  }, [appLiberado]);

  // Tutorial automático na 1ª vez (só app da loja)
  useEffect(() => {
    if (!appLiberado || modoCliente || !prontoParaSalvar || tourAutoRef.current) return;
    tourAutoRef.current = true;
    if (!tourJaConcluido()) {
      const t = window.setTimeout(() => setTourAtivo(true), 600);
      return () => window.clearTimeout(t);
    }
  }, [appLiberado, modoCliente, prontoParaSalvar]);

  // Link cliente: expira por inatividade (para de gerar request sem pedido)
  useEffect(() => {
    if (!modoCliente || !prontoParaSalvar || fluxoCliente !== 'ativo') return;
    const INATIVIDADE_MS = 12 * 60 * 1000;
    const marcarAtividade = () => {
      ultimaAtividadeRef.current = Date.now();
    };
    marcarAtividade();
    const eventos: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'touchstart',
      'scroll',
    ];
    for (const ev of eventos) {
      window.addEventListener(ev, marcarAtividade, { passive: true });
    }
    const id = window.setInterval(() => {
      if (Date.now() - ultimaAtividadeRef.current >= INATIVIDADE_MS) {
        setFluxoCliente('expirado');
      }
    }, 10_000);
    return () => {
      for (const ev of eventos) {
        window.removeEventListener(ev, marcarAtividade);
      }
      window.clearInterval(id);
    };
  }, [modoCliente, prontoParaSalvar, fluxoCliente]);

  useEffect(() => {
    if (!appLiberado || !prontoParaSalvar) return;
    if (modoCliente && fluxoCliente !== 'ativo') return;
    const ms = modoCliente ? 8000 : 20000;
    const id = window.setInterval(() => {
      void (async () => {
        const remoto = await puxarEstadoNuvem();
        if (!remoto) return;
        setEstado((prev) => aplicarCatalogoOficial(mesclarEstadosBarbearia(prev, remoto)));
      })();
    }, ms);
    return () => window.clearInterval(id);
  }, [appLiberado, prontoParaSalvar, modoCliente, fluxoCliente]);

  useEffect(() => {
    if (!appLiberado || !prontoParaSalvar || !carregouRef.current) return;
    if (modoCliente) return;
    const t = window.setTimeout(() => {
      void persistirEstadoBarbearia(estado);
      void publicarEstadoNuvem(estado);
    }, 500);
    return () => window.clearTimeout(t);
  }, [appLiberado, estado, prontoParaSalvar, modoCliente]);

  const patch = useCallback((fn: (prev: BarbeariaEstado) => BarbeariaEstado) => {
    setEstado((prev) => fn(prev));
  }, []);

  if (!appLiberado) {
    return <BarbeariaLicencaAtivacao onAtivado={() => setLicencaOk(true)} />;
  }

  const enviarLink = async () => {
    const link = montarLinkCliente(estado);
    const texto =
      `Agende seu horário na Carioca Barbershop (pelo site, sem instalar app):\n${link}`;
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share({
          title: 'Carioca Barbershop',
          text: texto,
          url: link,
        });
        setAvisoLink(
          'Link de agendamento enviado. Esse link é só para o cliente marcar horário — não pede chave e não instala o app.'
        );
        return;
      }
    } catch (e) {
      const name = e && typeof e === 'object' && 'name' in e ? String((e as { name: string }).name) : '';
      if (name === 'AbortError') return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
    setAvisoLink(
      'Abrindo WhatsApp com o link de agendamento (site). Chave de acesso é só no APK / instalador PC.'
    );
  };

  if (modoCliente) {
    return (
      <div className="space-y-5">
        <BarbeariaCabecalhoMarca />
        {!prontoParaSalvar ? (
          <p className="rounded-xl border-2 border-neutral-500 bg-white px-4 py-3 text-sm font-medium text-neutral-800">
            Carregando…
          </p>
        ) : fluxoCliente === 'expirado' ? (
          <div className="rounded-xl border-2 border-amber-800 bg-amber-50 px-4 py-5 text-center shadow-sm">
            <p className="text-base font-bold text-amber-950">Página expirada por inatividade</p>
            <p className="mt-2 text-sm text-amber-900">
              Os horários podem ter mudado. Atualize a página para continuar o agendamento com
              informações novas.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bb-btn-primary mt-4 px-4 py-2.5"
            >
              Atualizar página
            </button>
          </div>
        ) : (
          <BarbeariaAgendamento
            estado={estado}
            patch={patch}
            modoCliente
            onPedidoConfirmadoCliente={() => setFluxoCliente('confirmado')}
            onNovoPedidoCliente={() => {
              ultimaAtividadeRef.current = Date.now();
              setFluxoCliente('ativo');
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <BarbeariaCabecalhoMarca
        acoes={
          <>
            <button
              type="button"
              onClick={() => setTourAtivo(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-white/40 bg-black/45 px-3 py-2 text-xs font-bold text-white backdrop-blur-sm hover:bg-black/60"
              title="Ver tutorial das abas"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              Tutorial
            </button>
            <button
              type="button"
              data-tour="btn-link-cliente"
              onClick={() => void enviarLink()}
              className="inline-flex items-center gap-2 rounded-xl border border-white/40 bg-black/45 px-3 py-2 text-xs font-bold text-white backdrop-blur-sm hover:bg-black/60"
              title="Enviar link de agendamento (WhatsApp / compartilhar)"
            >
              <Share2 className="h-3.5 w-3.5" />
              Enviar link
            </button>
          </>
        }
      />
      {avisoLink && (
        <p className="break-all rounded-xl border border-amber-700 bg-amber-50 px-3 py-2 text-xs text-amber-950">
          {avisoLink}
        </p>
      )}

      <nav
        className={`grid gap-2 sm:gap-3 ${
          vistas.length >= 5 ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'
        }`}
        aria-label="Seções da barbearia"
        data-tour="nav-abas"
      >
        {vistas.map((v) => {
          const ativo = vista === v.id;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => setVista(v.id)}
              className={`bb-nav-btn ${
                ativo
                  ? 'border-amber-800 bg-amber-500 text-neutral-950 shadow-md ring-2 ring-amber-300'
                  : 'border-neutral-700 bg-neutral-100 text-neutral-900 hover:border-amber-700 hover:bg-amber-50'
              }`}
            >
              <v.icon className={ativo ? 'text-neutral-950' : 'text-amber-800'} />
              <span>{v.label}</span>
            </button>
          );
        })}
      </nav>

      {!prontoParaSalvar ? (
        <p className="rounded-xl border-2 border-neutral-500 bg-white px-4 py-3 text-sm font-medium text-neutral-800">
          Carregando barbearia…
        </p>
      ) : (
        <div className="space-y-4">
          {vista === 'agendamento' && (
            <BarbeariaAgendamento
              estado={estado}
              patch={patch}
              onConfirmadoNaLoja={() => setVista('admin')}
            />
          )}
          {vista === 'admin' && <BarbeariaAdmin estado={estado} patch={patch} />}
          {vista === 'financeiro' && <BarbeariaFinanceiro estado={estado} />}
          {vista === 'gastos' && <BarbeariaGastos estado={estado} patch={patch} />}
          {vista === 'licencas' && modoVendedor && (
            <BarbeariaPainelLicencas modoNwVendedor />
          )}
        </div>
      )}

      <BarbeariaTour
        ativo={tourAtivo}
        vista={vista}
        onMudarVista={setVista}
        onFechar={() => setTourAtivo(false)}
      />
      <BarbeariaLembretesWhatsApp estado={estado} patch={patch} />
    </div>
  );
}
